import { cx } from '@linaria/core';
import { useRef, useState } from 'react';
import { resets } from '@ui/css';
import { resolveComputedCssValues, verifyWCAGContrastRatio } from '@utils/css';
import { useIsomorphicLayoutEffect } from '@utils/react';
import { colors } from '../../../design-tokens/colors.js';
import { surfaces } from '../../../design-tokens/surfaces.js';
import { noMatches, list } from './_listbox.style.js';
import { listboxModifiers } from './_shared.style.js';
import { useListbox } from './hooks/listbox.js';
import { Option } from './option.listbox.js';
import { optionToId } from './utils/option-to-id.js';
import { jsxs, jsx } from 'react/jsx-runtime';

function Listbox(props) {
  const listboxRef = useRef(null);
  const toggleValue = (value, option) => {
    props.toggleValue(value, option);
    props.onChange?.((() => {
      if (!props.selectedValues) {
        // if no values were previously selected, then select the toggled
        // value as a starting point
        return [value];
      }
      if (props.selectedValues.includes(value)) {
        // if the toggled value was already selected, then remove it from the
        // user's selection
        return props.selectedValues.filter(it => it !== value);
      }

      // ... otherwise push the new selection to the list of user selections
      return [...props.selectedValues, value];
    })());
  };
  const {
    activeDescendantId,
    onOptionClick
  } = useListbox({
    ...props,
    toggleValue
  }, listboxRef);
  const [uncoloredLayout, setUncoloredLayout] = useState(false);

  // determine whether to use the uncolored layout for listboxes or not when
  // mounting
  useIsomorphicLayoutEffect(() => {
    if (!listboxRef.current) {
      return;
    }
    const resolvedColors = resolveComputedCssValues({
      inversedText: colors.text.inversed(),
      accentSurfaceBg: surfaces.accent.default.background()
    }, listboxRef.current);
    const canUseInvertedTextOnSecondarySurface = verifyWCAGContrastRatio(resolvedColors.inversedText, resolvedColors.accentSurfaceBg, {
      threshold: "AA-laxed"
    });
    setUncoloredLayout(!canUseInvertedTextOnSecondarySurface);
    listboxRef.current.classList.toggle(listboxModifiers.uncolored.className, !canUseInvertedTextOnSecondarySurface);
  }, []);
  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/aria-activedescendant-has-tabindex
    jsxs("ul", {
      id: props.id,
      ref: listboxRef,
      "data-theme": props.theme,
      className: cx(resets.rootCls, list, uncoloredLayout && listboxModifiers.uncolored.className, props.className),
      role: "listbox",
      "aria-labelledby": props.labelledBy,
      "aria-multiselectable": props.multiSelect,
      "aria-activedescendant": activeDescendantId,
      children: [props.options.map((option, index) => /*#__PURE__*/jsx(Option, {
        tabIndex: activeDescendantId === optionToId(props.id, option) ? 0 : -1,
        baseId: props.id,
        selectedValues: props.selectedValues,
        value: option,
        onClick: () => onOptionClick(index),
        multiSelect: props.multiSelect ?? false,
        disabled: typeof option === "object" ? option.disabled : undefined,
        disableIcon: props.disableIcons,
        defaultIcon: props.defaultIcon,
        toggledIcon: props.toggledIcon
      }, typeof option === "object" ? option.id : option)), props.options.length === 0 && /*#__PURE__*/jsx("li", {
        className: noMatches,
        children: "Vi fandt ikke nogen valgmuligheder."
      })]
    })
  );
}

export { Listbox };
//# sourceMappingURL=_listbox.js.map
